import { ReactElement, useEffect, useRef, useState } from 'react';
import { Box, Button, Loader, Text, TextField } from '@hopskipdrive/react-components';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import SendIcon from '@mui/icons-material/Send';

import { IncidentIndividual, IncidentNote } from '../../types/incidents';
import { AssigneeAvatar } from '../AssigneeSelect';

import { Note, NotesContent, NotesFooter, NotesInputContainer, NotesTitle } from './NotesInput.styles';

interface NotesInputProps {
  value: IncidentNote[] | null;
  onSubmit: (value?: string) => void;
  isLoading: boolean;
  placeholder: string;
  title?: string;
  titleIcon?: ReactElement;
}

export const NotesInput = ({
  value,
  onSubmit,
  isLoading,
  placeholder,
  title,
  titleIcon,
}: NotesInputProps): ReactElement => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [newNote, setNewNote] = useState<string>();
  const notesContentRef = useRef<HTMLDivElement>(null);

  const combineFirstAndLastName = (assignee: IncidentIndividual | null) => {
    return assignee ? assignee.firstName + ' ' + assignee.lastName : '';
  };

  const formatDateTimeString = (date: Date) => {
    const dateString = date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const timeString = date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });

    return dateString + ' | ' + timeString;
  };

  const getTimePassed = (current: Date, comparison: Date) => {
    const timeDifference = current.getTime() - comparison.getTime();

    const minutes = Math.floor(timeDifference / (1000 * 60));
    const hours = Math.floor(timeDifference / (1000 * 60 * 60));
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    let timeAgo = '';

    if (minutes < 60) {
      if (minutes < 0) {
        timeAgo = '< 1 min ago';
      } else {
        timeAgo = `(${minutes} min ago)`;
      }
    } else if (hours < 24) {
      timeAgo = `(${hours} hour${hours > 1 ? 's' : ''} ago)`;
    } else {
      timeAgo = `(${days} day${days > 1 ? 's' : ''} ago)`;
    }

    return timeAgo;
  };

  const addNote = () => {
    onSubmit(newNote);
    setNewNote('');
  };

  useEffect(() => {
    setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
  }, []);

  useEffect(() => {
    notesContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [value]);

  return (
    <NotesInputContainer>
      {title && (
        <NotesTitle>
          {titleIcon}
          <Text variant="h6">{title}</Text>
        </NotesTitle>
      )}

      <NotesContent ref={notesContentRef}>
        {isLoading && <Loader />}
        {value &&
          Array.isArray(value) &&
          value
            .slice()
            .reverse()
            .map((item) => (
              <Note key={item.id}>
                <Box className="author">
                  <AssigneeAvatar assignee={item.employee} />
                  <Box className="name">{combineFirstAndLastName(item.employee)}</Box>
                </Box>

                <Box className="timestamp">
                  <QueryBuilderIcon />
                  <Text variant="subtitle2">
                    {formatDateTimeString(new Date(item.createdAt))}{' '}
                    {getTimePassed(currentTime, new Date(item.createdAt))}
                  </Text>
                </Box>

                <Box className="content">
                  <Text variant="body1">{item.content}</Text>
                </Box>
              </Note>
            ))}
        {(!value || !value.length) && (
          <Text variant="caption" style={{ opacity: 0.5 }}>
            No notes for this incident yet
          </Text>
        )}
      </NotesContent>
      <NotesFooter>
        <TextField
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addNote();
            }
          }}
          fullWidth
          InputProps={{
            endAdornment: (
              <Button className="add" color="primary" disabled={!newNote} onClick={addNote}>
                <SendIcon />
                <Text variant="button">Add Note</Text>
              </Button>
            ),
          }}
          variant="outlined"
          placeholder={placeholder}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
        />
      </NotesFooter>
    </NotesInputContainer>
  );
};
